// Hotel bookings list. Same .bookings-wrap / .booking-row layout as the flight
// one, with the route column replaced by the property and stay dates.
//
// `/v1/bookings/` is cross-vertical — one table serves both — so this asks for
// `?product=hotel`. Server-side, deliberately: a client-side filter over a mixed
// page can only report the mixed count, so the pager would claim "1–25 of 40" on
// a list showing nine stays.

import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api } from "../lib/api";
import { useBodyClass } from "../lib/useBodyClass";
import { fmtDate, nightsLabel, starLabel } from "../lib/hotelFormat";
import { SkeletonBookingRow } from "../components/Skeletons";
import type { HotelBookingSummary } from "../lib/hotelTypes";
import type { BookingListItem, BookingStatus, Paginated } from "../lib/types";

const PAGE_SIZE = 25;

type FilterKey = "" | "confirmed" | "upcoming" | "drafts" | "cancelled";
const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: "", label: "All stays" },
  { key: "confirmed", label: "Confirmed" },
  { key: "upcoming", label: "Upcoming" },
  { key: "drafts", label: "Drafts & unpaid" },
  { key: "cancelled", label: "Cancelled" },
];

const STATUS_PILL: Record<BookingStatus, { cls: string; label: string }> = {
  draft:           { cls: "draft",     label: "Draft" },
  passenger_saved: { cls: "pending",   label: "Guests saved" },
  priced:          { cls: "pending",   label: "Re-priced" },
  confirmed:       { cls: "confirmed", label: "Confirmed" },
  cancelled:       { cls: "refunded",  label: "Cancelled" },
  failed:          { cls: "failed",    label: "Failed" },
};

export function HotelBookings() {
  useBodyClass("has-bg2");
  const [offset, setOffset] = useState(0);
  const [filter, setFilter] = useState<FilterKey>("");

  const q = useQuery({
    queryKey: ["bookings", "hotel", offset],
    queryFn: () => {
      const params = new URLSearchParams({
        product: "hotel", limit: String(PAGE_SIZE), offset: String(offset),
      });
      return api.get<Paginated<BookingListItem>>(`/v1/bookings/?${params}`);
    },
  });

  // The status chips stay client-side: they're a view over the page, and the
  // "upcoming" one needs a check-in date the backend doesn't index on.
  const rows = (q.data?.results || []).filter((b) => matches(b, filter));

  return (
    <div className="container">
      <div className="bookings-wrap">
        <div className="page-head">
          <h1>My <span className="serif">stays</span>.</h1>
          <p className="lede">
            Every hotel you've booked, quoted, or cancelled — confirmed stays on top,
            unfinished quotes behind the filter.
          </p>
        </div>

        <div className="filter-chip-row">
          {FILTERS.map((f) => (
            <button
              key={f.key || "all"}
              type="button"
              className={`qpill${filter === f.key ? " on" : ""}`}
              onClick={() => { setFilter(f.key); setOffset(0); }}
            >
              {f.label}
            </button>
          ))}
        </div>

        {q.isLoading && (
          <div className="bookings-list">
            <SkeletonBookingRow /><SkeletonBookingRow /><SkeletonBookingRow />
          </div>
        )}
        {q.error && <div className="app-error">Couldn't load bookings.</div>}

        {q.data && (
          <>
            <div className="bookings-list">
              <div className="booking-row head hotel-row">
                <span>Reference</span>
                <span>Property</span>
                <span>Stay</span>
                <span>Guests</span>
                <span>Status</span>
                <span style={{ textAlign: "right" }}>Amount</span>
                <span />
              </div>

              {rows.length === 0 && (
                <div className="booking-row" style={{ display: "block", textAlign: "center", color: "var(--ink-3)" }}>
                  No stays match this filter.{" "}
                  <Link to="/" style={{ textDecoration: "underline", color: "var(--ink-2)" }}>
                    Search for a hotel.
                  </Link>
                </div>
              )}

              {rows.map((b) => {
                const pill = STATUS_PILL[b.status] || STATUS_PILL.draft;
                const stay = (b.hotel_details || {}) as HotelBookingSummary;
                const stars = starLabel(stay.star_rating);
                const guests = (b.passengers || []) as Array<Record<string, unknown>>;
                const leadName = guests.length
                  ? `${guests[0].first_name ?? ""} ${guests[0].last_name ?? ""}`.trim()
                  : "";
                const roomCount = stay.guest_count?.rooms ?? stay.occupancies?.length ?? 0;

                return (
                  <Link to={`/bookings/${b.id}`} className="booking-row hotel-row" key={b.id}>
                    <div className="b-pnr">
                      {/* Whatever the front desk can look up, if we have it. An
                          unbooked row has neither — the status pill already
                          says which stage it stalled at, so don't relabel it. */}
                      <b>{stay.hotel_confirmation_code || b.pnr || "—"}</b>
                      <span title={b.id} style={{ fontFamily: "ui-monospace, SFMono-Regular, monospace" }}>
                        {b.id.slice(0, 8)}…{b.id.slice(-4)}
                      </span>
                    </div>
                    <div className="b-route">
                      <b>
                        {stars && <span className="hotel-stars">{stars}</span>}
                        {stay.name || "—"}
                      </b>
                      <span>{[stay.city_name, stay.room_name].filter(Boolean).join(" · ")}</span>
                    </div>
                    <div className="b-pax">
                      {stay.check_in ? `${fmtDate(stay.check_in)} → ${fmtDate(stay.check_out)}` : "—"}
                      <span style={{ fontSize: 11, color: "var(--ink-4)" }}>
                        {stay.nights ? nightsLabel(stay.nights) : ""}
                      </span>
                    </div>
                    <div className="b-pax">
                      {leadName || `${guests.length || "?"} guests`}
                      <span style={{ fontSize: 11, color: "var(--ink-4)" }}>
                        {roomCount ? `${roomCount} ${roomCount === 1 ? "room" : "rooms"}` : ""}
                      </span>
                    </div>
                    <span>
                      <span className={`status-pill ${pill.cls}`}><span className="dot" />{pill.label}</span>
                    </span>
                    <span className="b-amt">
                      {b.amount_charged ? fmtAmount(b.amount_charged, b.currency) : "—"}
                    </span>
                    <span className="b-go">→</span>
                  </Link>
                );
              })}
            </div>

            {/* `count` is the server's hotel-only total; the status chips
                narrow what's rendered from this page without changing it. */}
            <Pagination
              count={q.data.count}
              offset={offset}
              pageSize={PAGE_SIZE}
              onChange={setOffset}
            />
          </>
        )}
      </div>
    </div>
  );
}

function matches(b: BookingListItem, f: FilterKey): boolean {
  if (!f) return true;
  if (f === "confirmed") return b.status === "confirmed";
  if (f === "upcoming") {
    // A real "upcoming" needs the check-in date, which the summary carries —
    // unlike the flight list, which has no departure date to filter on.
    const stay = (b.hotel_details || {}) as HotelBookingSummary;
    if (b.status !== "confirmed" || !stay.check_in) return false;
    return new Date(`${stay.check_in}T00:00:00`).getTime() >= Date.now();
  }
  if (f === "drafts") return ["draft", "passenger_saved", "priced"].includes(b.status);
  if (f === "cancelled") return b.status === "cancelled" || b.status === "failed";
  return true;
}

function Pagination(props: {
  count: number; offset: number; pageSize: number; onChange: (o: number) => void;
}) {
  const { count, offset, pageSize, onChange } = props;
  if (count <= pageSize) return null;
  return (
    <div className="pagination">
      <span className="info">
        Showing <b>{offset + 1}–{Math.min(offset + pageSize, count)}</b> of {count}
      </span>
      <div className="pager">
        <button type="button" disabled={offset === 0}
                onClick={() => onChange(Math.max(0, offset - pageSize))}>←</button>
        <button type="button" disabled={offset + pageSize >= count}
                onClick={() => onChange(offset + pageSize)}>→</button>
      </div>
    </div>
  );
}

function fmtAmount(amount: string, currency: string): string {
  const n = parseFloat(amount);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency", currency, maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${currency} ${Math.round(n).toLocaleString("en-IN")}`;
  }
}
